import React, { useState, useEffect } from 'react';
import { useTasks } from '../../hooks/useTasks';
import { useBoards } from '../../hooks/useBoards';
import { useTaskTags } from '../../hooks/tasks/useTaskTags';
import { useTaskFilters } from '../../hooks/tasks/useTaskFilters';
import TasksToolbar from './TasksToolbar';
import KanbanBoard from './KanbanBoard';
import TagManager from './TagManager';
import useStore from '../../utils/store';
import TaskCreateDialog from './TaskCreateDialog';
import { listTaskGroupsApi } from '../../services/tasksService';

const BoardView = ({ auth, workspaceId, board, onToggleSidebar, members = [] }) => {
    // ... (existing hooks)

    // ...


    const { updateTask, createTask, reorderTasks, assignTag, removeTag, deleteTask, addReminder, deleteReminder, fetchProjects, projects } = useTasks({
        token: auth?.user?.access_token,
        workspaceId
    });

    const { fetchBoardTasks, updateBoard, fetchBoardStatuses, createStatus, updateStatus, deleteStatus } = useBoards({
        token: auth?.user?.access_token,
        workspaceId
    });

    const subscription = useStore((state) => state.subscription);
    const isPro = subscription?.plan_name === 'Premium' || subscription?.plan_name === 'Pro';

    const [boardTasks, setBoardTasks] = useState([]);
    const [boardStatuses, setBoardStatuses] = useState([]);
    const [allTaskGroups, setAllTaskGroups] = useState([]); // Aggregated groups from all relevant projects

    // Tags
    const { tags, fetchTags, createTag, updateTag, deleteTag } = useTaskTags({
        token: auth?.user?.access_token,
        workspaceId
    });

    // View State
    const [filterTags, setFilterTags] = useState([]); // Array of tag IDs
    const [filterProjectIds, setFilterProjectIds] = useState([]);
    const [filterCategoryIds, setFilterCategoryIds] = useState([]);
    const [filterPriorities, setFilterPriorities] = useState([]);
    const [filterAttributes, setFilterAttributes] = useState([]);
    const [isManageTags, setIsManageTags] = useState(false);

    // Create Dialog State
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [createDialogStatusId, setCreateDialogStatusId] = useState(null);
    const [createDialogStatusName, setCreateDialogStatusName] = useState('');

    // Use extracted filter hook
    const { filteredTasks } = useTaskFilters(boardTasks, [], filterTags, filterProjectIds, filterCategoryIds, filterPriorities, filterAttributes);

    useEffect(() => {
        if (board?.id) {
            loadBoardTasks();
        }
    }, [board?.id]);

    useEffect(() => {
        if (workspaceId) {
            fetchTags();
            fetchProjects();
        }
    }, [workspaceId]);

    // Fetch Task Groups for projects present in the board
    useEffect(() => {
        const loadGroups = async () => {
            if (!boardTasks.length) return;

            const uniqueProjectIds = [...new Set(boardTasks.map(t => t.project_id).filter(Boolean))];
            if (uniqueProjectIds.length === 0) return;

            try {
                const groupsPromises = uniqueProjectIds.map(pid =>
                    listTaskGroupsApi({ token: auth?.user?.access_token, workspaceId, projectId: pid })
                );
                const results = await Promise.all(groupsPromises);
                // results is array of arrays of groups
                const flatGroups = results.flat();
                // Remove duplicates if any (unlikely unless project ID repeated)
                // Map to ensure they have project info if needed? 
                // listTaskGroupsApi returns groups. They might not have project_name, loop to add it if needed?
                // But for now just storing them is enough to match IDs.
                setAllTaskGroups(flatGroups);
            } catch (err) {
                console.error("Failed to load task groups for board", err);
            }
        };

        loadGroups();
    }, [boardTasks, auth, workspaceId]);

    const loadBoardTasks = async () => {
        const [tasksData, statusesData] = await Promise.all([
            fetchBoardTasks(board.id),
            fetchBoardStatuses(board.id)
        ]);
        setBoardTasks(tasksData || []);
        setBoardStatuses(statusesData || []);
    };

    if (!board) return null;

    // Local State Wrappers for Optimistic Updates
    const handleUpdateTask = async (taskId, updates) => {
        setBoardTasks(prev => prev.map(t => {
            if (t.id === taskId) {
                const newT = { ...t, ...updates };
                // Map API/dnd-kit camelCase to DB snake_case for local view
                if (updates.statusId !== undefined) newT.status_id = updates.statusId;
                if (updates.isCompleted !== undefined) newT.is_completed = updates.isCompleted;
                if (updates.assignedTo !== undefined) newT.assigned_to = updates.assignedTo;
                return newT;
            }
            return t;
        }));
        await updateTask(taskId, updates);
    };

    const handleReorderTasks = async (updates) => {
        setBoardTasks(prev => {
            const newTasks = [...prev];
            updates.forEach(u => {
                const t = newTasks.find(task => task.id === u.id);
                if (t) {
                    if (u.sortOrder !== undefined) t.sort_order = u.sortOrder;
                    if (u.section_id !== undefined) t.section_id = u.section_id;
                }
            });
            // Re-sort to match backend sorting logic so drag doesn't snap back
            return newTasks.sort((a, b) => {
                // Completed at bottom
                if (!!a.is_completed !== !!b.is_completed) {
                    return a.is_completed ? 1 : -1;
                }
                // Then by sort order
                return (a.sort_order || 0) - (b.sort_order || 0);
            });
        });
        await reorderTasks(updates);
    };

    const handleCreateTask = async (data) => {
        const newTask = await createTask(data);
        if (newTask) {
            setBoardTasks(prev => [newTask, ...prev]);
        }
    };

    const handleDeleteTask = async (taskId) => {
        setBoardTasks(prev => prev.filter(t => t.id !== taskId));
        await deleteTask(taskId);
    };

    const handleAssignTag = async (taskId, tag) => {
        setBoardTasks(prev => prev.map(t => {
            if (t.id === taskId) {
                const currentTags = t.tags || [];
                if (currentTags.find(ct => ct.id === tag.id)) return t;
                return { ...t, tags: [...currentTags, tag] };
            }
            return t;
        }));
        await assignTag(taskId, tag);
    };

    const handleRemoveTag = async (taskId, tagId) => {
        setBoardTasks(prev => prev.map(t => {
            if (t.id === taskId) {
                return { ...t, tags: (t.tags || []).filter(ct => ct.id !== tagId) };
            }
            return t;
        }));
        await removeTag(taskId, tagId);
    };

    const handleRequestCreateTask = (statusId, statusName) => {
        setCreateDialogStatusId(statusId);
        setCreateDialogStatusName(statusName);
        setShowCreateDialog(true);
    };

    const handleDialogSubmit = async (taskData) => {
        await handleCreateTask({ ...taskData, projectId: board.id });
    };

    return (
        <div className="d-flex flex-column h-100 w-100 bg-white">
            <TasksToolbar
                board={board}
                onManageTags={() => setIsManageTags(!isManageTags)}
                tags={tags}
                projects={projects}
                taskGroups={allTaskGroups}
                filterTags={filterTags}
                setFilterTags={setFilterTags}
                filterProjectIds={filterProjectIds}
                setFilterProjectIds={setFilterProjectIds}
                filterCategoryIds={filterCategoryIds}
                setFilterCategoryIds={setFilterCategoryIds}
                filterPriorities={filterPriorities}
                setFilterPriorities={setFilterPriorities}
                filterAttributes={filterAttributes}
                setFilterAttributes={setFilterAttributes}
                onUpdateBoard={updateBoard}
                auth={auth}
                workspaceId={workspaceId}
                onToggleSidebar={onToggleSidebar}
            />

            {/* Tag Management Panel */}
            {isManageTags && <TagManager tags={tags} onCreateTag={createTag} onUpdateTag={updateTag} onDeleteTag={deleteTag} />}

            <div className="flex-grow-1 overflow-auto p-2 p-md-4 custom-scrollbar">
                <KanbanBoard
                    tasks={filteredTasks}
                    taskStatuses={boardStatuses}
                    reorderTasks={handleReorderTasks}
                    updateTask={handleUpdateTask}
                    createTask={handleCreateTask}
                    createStatus={createStatus}
                    updateStatus={updateStatus}
                    deleteStatus={deleteStatus}
                    boardId={board.id}
                    onDeleteTask={handleDeleteTask}
                    allTags={tags}
                    onAssignTag={handleAssignTag}
                    onRemoveTag={handleRemoveTag}
                    onCreateSubtask={() => { }} // Subtasks might need project ID?
                    onAddReminder={addReminder}
                    onDeleteReminder={deleteReminder}
                    isPro={isPro}
                    onRequestCreateTask={handleRequestCreateTask}
                    members={members}
                />
            </div>

            <TaskCreateDialog
                show={showCreateDialog}
                onHide={() => setShowCreateDialog(false)}
                onSubmit={handleDialogSubmit}
                initialStatusId={createDialogStatusId}
                statusName={createDialogStatusName}
                members={members}
            />
        </div>
    );
};

export default BoardView;
